import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';

export interface TabDef {
  key: string;
  label: string;
}

/** Tabs kept in the address (`?tab=`), so a tab can be linked to and the back button works. */
export function useTab(tabs: readonly TabDef[]): [string, (key: string) => void] {
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab');
  const current = tabs.find((x) => x.key === wanted)?.key ?? tabs[0]?.key ?? '';
  return [current, (key) => setParams({ tab: key }, { replace: true })];
}

export function Tabs({
  tabs,
  current,
  onChange,
  label,
  children,
}: {
  tabs: readonly TabDef[];
  current: string;
  onChange: (key: string) => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div role="tablist" aria-label={label} className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200">
        {tabs.map((x) => {
          const active = x.key === current;
          return (
            <button
              key={x.key}
              type="button"
              role="tab"
              id={`tab-${x.key}`}
              aria-selected={active}
              aria-controls={`panel-${x.key}`}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(x.key)}
              onKeyDown={(e) => {
                const i = tabs.findIndex((y) => y.key === current);
                const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                if (!step) return;
                e.preventDefault();
                const next = tabs[(i + step + tabs.length) % tabs.length];
                if (next) {
                  onChange(next.key);
                  document.getElementById(`tab-${next.key}`)?.focus();
                }
              }}
              className={`-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium ${
                active ? 'border-brand text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {x.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`panel-${current}`} aria-labelledby={`tab-${current}`}>
        {children}
      </div>
    </div>
  );
}
